// Adapter: showcase item -> booking draft.
//
// Two surfaces can start a booking · the docked slider card and the immersive
// view · and they must produce byte-identical drafts, so the mapping lives
// here rather than at either call site. It is also the one place that will
// need touching when the id contract (plan P1) starts carrying room_id /
// rate_id, instead of two places that could drift.

import { getImageSrc, ShowcaseItem } from "../templates/utils/showcase";
import { isMobileViewport } from "../../../../../utils/viewport";
import { useSliderState } from "../SliderState";
import useBookingState from "./BookingState";

/**
 * True when this item is something a guest could actually buy. `bookUrl` is
 * what marks a showcase item as coming from a quote (showcase.ts sets it only
 * for `type === "quote"`), and without rates there is nothing to pick.
 */
export const isBookableItem = (item?: ShowcaseItem | null): boolean =>
  !!item?.bookUrl && !!item?.rates?.length;

export const startBookingFromShowcase = (
  item: ShowcaseItem,
  template: any,
  rateIndex?: number
) => {
  if (!item.rates?.length) return;

  // roomId/rateId stay null until the id contract lands end to end (plan P1):
  // dengine's QuoteTemplate carries neither today, so the draft records titles
  // and the driver has nothing stable to match on. The P0 spike found Scidoo's
  // own ids ARE stable and addressable (data-categoria / data-retta), which is
  // exactly what P1 has to start carrying through.
  const rates = item.rates.map((rate) => ({
    id: null,
    title: rate.title,
    price: rate.price,
    conditions: rate.conditions,
  }));

  useBookingState.getState().start({
    roomId: null,
    roomTitle: item.title,
    imageUrl: item.images?.[0] ? getImageSrc(item.images[0]) : null,
    rates,
    rateIndex: rateIndex ?? null,
    stay: {
      checkin: template?.checkin ?? null,
      checkout: template?.checkout ?? null,
      nights: template?.nights ?? null,
      adults: template?.adults ?? null,
      children: template?.children ?? null,
    },
    guestName: template?.guest_name ?? null,
    guestPhone: template?.guest_phone ?? null,
  });

  // Starting a booking has to also SHOW it, and where it shows differs by
  // viewport. This used to be missing entirely: the docked card flipped
  // `isActive` and nothing appeared, because BookingPanel only ever lived
  // inside the immersive overlay and the docked slider had no case for it.
  //
  // Desktop: raise the immersive surface · there is real room there, and the
  // chosen room stays on screen beside the form.
  // Mobile: do nothing. The docked sheet is already the full-screen surface,
  // and it now renders the booking itself (Slider.renderContent). Raising an
  // overlay would be a second road to the same place.
  if (!isMobileViewport()) {
    useSliderState.getState().setExpanded(true);
  }
};
